import { Link } from 'react-router-dom';
import ArticleLayout from '../components/ArticleLayout';

const faqItems = [
  {
    question: 'Itaobuy Spreadsheet è legale?',
    answer: 'Assolutamente sì. Itaobuy Spreadsheet è una piattaforma informativa che aggrega dati pubblicamente disponibili. Non viola alcuna legge e opera nella piena trasparenza.',
  },
  {
    question: 'Quanto tempo ci vuole per ricevere i prodotti?',
    answer: 'I tempi di consegna variano in base al venditore e alla destinazione. In media, per l\'Italia, si aggirano tra 7 e 21 giorni lavorativi.',
  },
  {
    question: 'Posso restituire un prodotto se non mi piace?',
    answer: 'Dipende dalle politiche del singolo venditore. Itaobuy Spreadsheet ti aiuta a identificare i venditori con le migliori politiche di reso.',
  },
  {
    question: 'Devo pagare per usare Itaobuy Spreadsheet?',
    answer: 'No, l\'accesso al database e alle guide di Itaobuy Spreadsheet è completamente gratuito. Paghi solo i prodotti che decidi di acquistare.',
  },
  {
    question: 'Come faccio a sapere se un prodotto è di buona qualità?',
    answer: 'Affidati alle recensioni degli altri utenti e alle valutazioni presenti nel database. I prodotti con valutazioni superiori a 4 stelle e molte recensioni sono generalmente di ottima qualità.',
  },
];

export default function ArticlePage() {
  return (
    <ArticleLayout
      title="Itaobuy Sheet Guida Completa 2026: Come Trovare i Migliori Prodotti Online"
      description="Una guida approfondita per capire cos'è Itaobuy Spreadsheet, come funziona, e come utilizzarlo al meglio per risparmiare tempo e denaro nei tuoi acquisti online. Tutto spiegato in italiano, passo dopo passo."
      tag="Guida Completa"
      slug="itaobuy-spreadsheet-guida-completa"
      faqItems={faqItems}
    >
      <div className="bg-accent-50 border border-accent-200/50 rounded-xl p-6 mb-12">
        <p className="text-sm font-semibold text-accent-800 mb-2 font-label">TL;DR &mdash; In Breve</p>
        <p className="text-sm text-accent-700 leading-relaxed">
          <strong>Itaobuy Spreadsheet</strong> è uno strumento essenziale per chi fa shopping online internazionale.
          Ti permette di confrontare prezzi, trovare prodotti di qualità e risparmiare fino al 40% rispetto
          all&apos;acquisto diretto. In questa guida imparerai tutto: dalla configurazione iniziale alle
          strategie avanzate per utenti esperti.
        </p>
      </div>

      <nav className="bg-background-100 rounded-xl p-6 mb-12">
        <p className="text-sm font-semibold text-foreground-800 mb-3 font-label">Indice dei Contenuti</p>
        <ol className="space-y-2 text-sm text-foreground-600">
          <li>1. <a href="#cos-e" className="text-primary-600 hover:text-primary-700">Cos&apos;è Itaobuy Spreadsheet</a></li>
          <li>2. <a href="#come-funziona" className="text-primary-600 hover:text-primary-700">Come Funziona</a></li>
          <li>3. <a href="#vantaggi" className="text-primary-600 hover:text-primary-700">I Vantaggi</a></li>
          <li>4. <a href="#guida-pratica" className="text-primary-600 hover:text-primary-700">Guida Pratica per Principianti</a></li>
          <li>5. <a href="#confronto" className="text-primary-600 hover:text-primary-700">Confronto con le Alternative</a></li>
          <li>6. <a href="#sicurezza" className="text-primary-600 hover:text-primary-700">Sicurezza</a></li>
          <li>7. <a href="#pro-contro" className="text-primary-600 hover:text-primary-700">Pro e Contro</a></li>
          <li>8. <a href="#strategie" className="text-primary-600 hover:text-primary-700">Strategie Avanzate</a></li>
          <li>9. <a href="#faq" className="text-primary-600 hover:text-primary-700">Domande Frequenti (FAQ)</a></li>
          <li>10. <a href="#conclusione" className="text-primary-600 hover:text-primary-700">Conclusione</a></li>
        </ol>
      </nav>

      <section id="cos-e" className="mb-14">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Cos&apos;è Itaobuy Spreadsheet</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          <strong>Itaobuy Spreadsheet</strong> è una piattaforma innovativa progettata per semplificare
          lo shopping online internazionale. Nato dall&apos;esperienza diretta di acquirenti italiani
          che cercavano un modo più efficiente per trovare prodotti di qualità a prezzi competitivi.
        </p>
        <p className="text-foreground-700 leading-relaxed mb-4">
          A differenza dei tradizionali motori di ricerca per lo shopping, Itaobuy Spreadsheet
          organizza i prodotti in un formato spreadsheet intuitivo, permettendoti di confrontare
          prezzi, valutazioni e caratteristiche in un&apos;unica schermata.
        </p>
        <p className="text-foreground-700 leading-relaxed">
          La piattaforma è particolarmente apprezzata dalla community italiana per la sua completezza:
          copre <strong>14 categorie</strong> di prodotti e oltre <strong>15.420 prodotti</strong> catalogati.
        </p>
      </section>

      <section id="come-funziona" className="mb-14">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Come Funziona Itaobuy Spreadsheet</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Il funzionamento è sorprendentemente semplice, anche per chi non ha esperienza. Ecco il processo in tre fasi:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 my-8">
          <div className="bg-background-100 rounded-xl p-5">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mb-3">
              <span className="text-primary-700 font-bold text-lg">1</span>
            </div>
            <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">Seleziona Categoria</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">Scegli tra 14 categorie ottimizzate.</p>
          </div>
          <div className="bg-background-100 rounded-xl p-5">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mb-3">
              <span className="text-primary-700 font-bold text-lg">2</span>
            </div>
            <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">Esplora Prodotti</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">Sfoglia, confronta prezzi e leggi recensioni.</p>
          </div>
          <div className="bg-background-100 rounded-xl p-5">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mb-3">
              <span className="text-primary-700 font-bold text-lg">3</span>
            </div>
            <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">Acquista Sicuro</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">Segui i consigli e completa l&apos;acquisto.</p>
          </div>
        </div>
      </section>

      <section id="vantaggi" className="mb-14">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">I Vantaggi di Usare Itaobuy Spreadsheet</h2>
        <ul className="space-y-4 mb-6">
          <li className="flex gap-3">
            <i className="ri-check-line text-primary-600 mt-0.5 flex-shrink-0"></i>
            <div>
              <strong className="text-foreground-800">Risparmio Economico Significativo</strong>
              <p className="text-sm text-foreground-600 mt-1">In media, gli utenti risparmiano tra il 30% e il 50%.</p>
            </div>
          </li>
          <li className="flex gap-3">
            <i className="ri-check-line text-primary-600 mt-0.5 flex-shrink-0"></i>
            <div>
              <strong className="text-foreground-800">Database Aggiornato 24/7</strong>
              <p className="text-sm text-foreground-600 mt-1">I prodotti vengono aggiornati continuamente.</p>
            </div>
          </li>
          <li className="flex gap-3">
            <i className="ri-check-line text-primary-600 mt-0.5 flex-shrink-0"></i>
            <div>
              <strong className="text-foreground-800">Interfaccia in Italiano</strong>
              <p className="text-sm text-foreground-600 mt-1">Tutte le guide sono scritte in italiano da madrelingua.</p>
            </div>
          </li>
        </ul>
      </section>

      <section id="guida-pratica" className="mb-14">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Guida Pratica per Principianti</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Se è la prima volta che usi <Link to="/" className="text-primary-600 hover:text-primary-700">Itaobuy Spread</Link>, ecco come iniziare:
        </p>
        <h3 className="font-heading text-xl font-semibold text-foreground-900 mt-8 mb-3">Passo 1: Familiarizza con l&apos;Interfaccia</h3>
        <p className="text-foreground-700 leading-relaxed mb-4">Prenditi qualche minuto per esplorare le diverse sezioni. La navigazione è intuitiva.</p>
        <h3 className="font-heading text-xl font-semibold text-foreground-900 mt-8 mb-3">Passo 2: Scegli la Tua Prima Categoria</h3>
        <p className="text-foreground-700 leading-relaxed mb-4">Inizia con una categoria che conosci bene, come Shoes o T-Shirts.</p>
        <h3 className="font-heading text-xl font-semibold text-foreground-900 mt-8 mb-3">Passo 3: Analizza i Dati</h3>
        <p className="text-foreground-700 leading-relaxed mb-4">Ogni prodotto è accompagnato da prezzo, valutazione e descrizione dettagliata.</p>
        <h3 className="font-heading text-xl font-semibold text-foreground-900 mt-8 mb-3">Passo 4: Completa il Tuo Primo Acquisto</h3>
        <p className="text-foreground-700 leading-relaxed">Segui le istruzioni per completare l&apos;acquisto in sicurezza.</p>
      </section>

      <section id="confronto" className="mb-14">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Confronto con le Alternative</h2>
        <div className="overflow-x-auto rounded-xl border border-background-200/70">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-background-100">
                <th className="text-left px-5 py-3.5 font-semibold text-foreground-800">Caratteristica</th>
                <th className="text-left px-5 py-3.5 font-semibold text-primary-700">Itaobuy Spreadsheet</th>
                <th className="text-left px-5 py-3.5 font-semibold text-foreground-600">Alternative</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200/70">
              <tr><td className="px-5 py-3 text-foreground-700">Prodotti</td><td className="px-5 py-3 text-primary-700 font-medium">15,420+</td><td className="px-5 py-3 text-foreground-600">Variabile</td></tr>
              <tr><td className="px-5 py-3 text-foreground-700">Categorie</td><td className="px-5 py-3 text-primary-700 font-medium">14</td><td className="px-5 py-3 text-foreground-600">8-12</td></tr>
              <tr><td className="px-5 py-3 text-foreground-700">Guide in Italiano</td><td className="px-5 py-3 text-primary-700 font-medium">Sì, complete</td><td className="px-5 py-3 text-foreground-600">Limitate</td></tr>
              <tr><td className="px-5 py-3 text-foreground-700">Aggiornamenti</td><td className="px-5 py-3 text-primary-700 font-medium">24/7</td><td className="px-5 py-3 text-foreground-600">Settimanali</td></tr>
              <tr><td className="px-5 py-3 text-foreground-700">Community Italiana</td><td className="px-5 py-3 text-primary-700 font-medium">Attiva</td><td className="px-5 py-3 text-foreground-600">Limitata</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      <section id="sicurezza" className="mb-14">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Itaobuy Spreadsheet è Sicuro?</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">Sì, Itaobuy Spreadsheet è sicuro. La piattaforma non gestisce direttamente le transazioni ma funge da intermediario informativo.</p>
        <ul className="space-y-3 mb-6">
          <li className="flex gap-3"><i className="ri-shield-check-line text-accent-600 mt-0.5 flex-shrink-0 text-lg"></i><p className="text-sm text-foreground-700"><strong>Verifica sempre le recensioni</strong> — Un prodotto con molte recensioni positive è generalmente affidabile.</p></li>
          <li className="flex gap-3"><i className="ri-shield-check-line text-accent-600 mt-0.5 flex-shrink-0 text-lg"></i><p className="text-sm text-foreground-700"><strong>Controlla le politiche di reso</strong> — Assicurati di conoscere le condizioni prima dell&apos;acquisto.</p></li>
          <li className="flex gap-3"><i className="ri-shield-check-line text-accent-600 mt-0.5 flex-shrink-0 text-lg"></i><p className="text-sm text-foreground-700"><strong>Usa metodi di pagamento tracciabili</strong> — Preferisci carte di credito o servizi con protezione acquirente.</p></li>
        </ul>
      </section>

      <section id="pro-contro" className="mb-14">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Pro e Contro</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-secondary-50 border border-secondary-200/50 rounded-xl p-6">
            <h3 className="font-heading text-lg font-semibold text-secondary-800 mb-4"><i className="ri-thumb-up-line"></i> Pro</h3>
            <ul className="space-y-3">
              <li className="flex gap-2 text-sm text-foreground-700"><i className="ri-check-line text-secondary-600 mt-0.5 flex-shrink-0"></i>Interfaccia intuitiva</li>
              <li className="flex gap-2 text-sm text-foreground-700"><i className="ri-check-line text-secondary-600 mt-0.5 flex-shrink-0"></i>Database vastissimo</li>
              <li className="flex gap-2 text-sm text-foreground-700"><i className="ri-check-line text-secondary-600 mt-0.5 flex-shrink-0"></i>Guide in italiano</li>
              <li className="flex gap-2 text-sm text-foreground-700"><i className="ri-check-line text-secondary-600 mt-0.5 flex-shrink-0"></i>Aggiornamenti costanti</li>
            </ul>
          </div>
          <div className="bg-accent-50 border border-accent-200/50 rounded-xl p-6">
            <h3 className="font-heading text-lg font-semibold text-accent-800 mb-4"><i className="ri-thumb-down-line"></i> Contro</h3>
            <ul className="space-y-3">
              <li className="flex gap-2 text-sm text-foreground-700"><i className="ri-close-line text-accent-500 mt-0.5 flex-shrink-0"></i>Curva di apprendimento iniziale</li>
              <li className="flex gap-2 text-sm text-foreground-700"><i className="ri-close-line text-accent-500 mt-0.5 flex-shrink-0"></i>Alcune categorie meno fornite</li>
              <li className="flex gap-2 text-sm text-foreground-700"><i className="ri-close-line text-accent-500 mt-0.5 flex-shrink-0"></i>Non disponibile come app nativa</li>
            </ul>
          </div>
        </div>
      </section>

      <section id="strategie" className="mb-14">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Strategie Avanzate</h2>
        <div className="space-y-6">
          <div className="bg-background-100 rounded-xl p-5">
            <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">Confronto Multi-Categoria</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">Prodotti simili sono spesso catalogati in categorie diverse con prezzi differenti.</p>
          </div>
          <div className="bg-background-100 rounded-xl p-5">
            <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">Monitoraggio Prezzi</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">Tieni traccia dei prodotti per qualche giorno prima di acquistare.</p>
          </div>
          <div className="bg-background-100 rounded-xl p-5">
            <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">Acquisti Combinati</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">Acquistando più prodotti dallo stesso venditore risparmi sulle spese di spedizione.</p>
          </div>
        </div>
      </section>

      <section id="faq" className="mb-14">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">Domande Frequenti (FAQ)</h2>
        <div className="space-y-4">
          {faqItems.map((faq, idx) => (
            <div key={idx} className="border border-background-200/70 rounded-xl p-5">
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{faq.question}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="conclusione" className="mb-14">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Conclusione</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Dopo mesi di utilizzo intensivo, la mia risposta è un convinto <strong>sì</strong>. Itaobuy Spreadsheet
          rappresenta la soluzione più completa per lo shopping online internazionale in italiano.
        </p>
        <p className="text-foreground-700 leading-relaxed">
          Torna alla <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">pagina principale di Itaobuy Spread</Link> per
          esplorare tutte le categorie e iniziare subito a risparmiare.
        </p>
      </section>
    </ArticleLayout>
  );
}